import type { LifecycleStatus } from '@/types/config-governance';

/**
 * The client mirror of the two identical state machines on core —
 * policy-state.machine.ts and form-state.machine.ts. They share statuses,
 * transitions and the reason rule by design (both govern real records made
 * under them), so one mirror serves both screens.
 *
 * Mirrored rather than fetched because the machine IS the button layout: a
 * screen that renders "Approve" on a DRAFT is lying about what the next click
 * will do, and the server's 409 arrives after the operator has already formed
 * the wrong model. The server remains the authority — an illegal move that
 * slips through (say, a stale page) is refused there with its own sentence,
 * which the page shows verbatim.
 */
export const LIFECYCLE_TRANSITIONS: Readonly<Record<LifecycleStatus, readonly LifecycleStatus[]>> =
  Object.freeze({
    DRAFT: ['READY_FOR_REVIEW'],
    // Back to DRAFT is a rejection, and rejections carry reasons — see below.
    READY_FOR_REVIEW: ['APPROVED', 'DRAFT'],
    // An approval can be reconsidered before activation; after it, it cannot.
    APPROVED: ['ACTIVE', 'DRAFT'],
    ACTIVE: ['SUPERSEDED'],
    SUPERSEDED: [],
  });

/**
 * Statuses that must be entered WITH an explanation, per
 * POLICY_REASON_REQUIRED_ON_ENTRY / FORM_REASON_REQUIRED_ON_ENTRY: DRAFT
 * because arriving there is a rejection or a withdrawn approval, SUPERSEDED
 * because it is the moment a governing rule stopped governing. Note that
 * SUPERSEDED is never offered as a button — supersession happens server-side
 * when a sibling activates — but the rule is mirrored completely so a future
 * caller cannot trip over the gap.
 */
export const LIFECYCLE_REASON_REQUIRED_ON_ENTRY: readonly LifecycleStatus[] = [
  'DRAFT',
  'SUPERSEDED',
];

export const allowedLifecycleTransitions = (from: LifecycleStatus): readonly LifecycleStatus[] =>
  LIFECYCLE_TRANSITIONS[from] ?? [];

export const lifecycleTransitionRequiresReason = (to: LifecycleStatus): boolean =>
  LIFECYCLE_REASON_REQUIRED_ON_ENTRY.includes(to);

/** DRAFT is the only editable status on both machines; everything else is record. */
export const isLifecycleEditable = (status: LifecycleStatus): boolean => status === 'DRAFT';
